/**
 * The one contract between the shell loader and the system-surfaces bundle (layered shell
 * surfaces, R-8, D18; WS-7 §3.1).
 *
 * The loader (WS-6) imports every name here from `@zion/shell-system-surfaces/protocol` and
 * bundles it; the bundle (WS-7) imports the same file. Neither declares any of these names
 * again. The loader asks {@link matchSystemRoute} which system surface a path is, mounts the
 * bundle at {@link SYSTEM_SURFACE_URL} with placement `system` and the stylesheet at
 * {@link SYSTEM_STYLESHEET_URL}, hands the surface a {@link SystemSurfaceInput}, and passes every
 * output through {@link readSystemOutput} before it adopts a session or navigates.
 *
 * The module imports `./navigation.js` and one schema of `@zion/sdk-contracts`,
 * `secondFactorWaitSchema` (WS-25, R-012). It imports no React. Every loader already links
 * `@zion/sdk-contracts` for its session parse, so the schema adds no framework to a loader. It
 * holds no state, does no I/O, and nothing in it throws.
 */
import { secondFactorWaitSchema, type SecondFactorWait } from '@zion/sdk-contracts';

import { resolvePostLoginPath } from './navigation.js';

export { buildLoginRedirectPath, resolvePostLoginPath } from './navigation.js';

// ── Names and URLs ──────────────────────────────────────────────────────────

/**
 * Every system surface, in the order the routes are listed. `setup` mounts at any path while
 * setup status reads `setup`, and `maintenance` mounts in the loader's overlay; neither has a
 * route. `not-found` answers an unmatched path under a system segment.
 */
export const SYSTEM_SURFACE_NAMES = Object.freeze([
  'login',
  'recovery-login',
  'oauth-callback',
  'oauth-consent',
  'logout',
  'verify-email',
  'password-reset-request',
  'password-reset-confirm',
  'accept-invitation',
  'change-password',
  'setup',
  'maintenance',
  'not-found',
] as const);

/** One system surface name. */
export type SystemSurfaceName = (typeof SYSTEM_SURFACE_NAMES)[number];

/** The bundle entry the loader imports, served by the shell's nginx `/system/` location. */
export const SYSTEM_SURFACE_URL = '/system/index.js';

/** The bundle stylesheet the loader links before it mounts a system surface. */
export const SYSTEM_STYLESHEET_URL = '/system/index.css';

/**
 * The first path segments the system surfaces own. A path under one of them that matches no
 * route answers `not-found`. Each is a `RESERVED_ROUTER_SEGMENTS` entry of `@zion/sdk-contracts`,
 * so no app can mount under one.
 */
export const SYSTEM_ROUTE_SEGMENTS = Object.freeze([
  'auth',
  'change-password',
  'invitations',
  'login',
  'logout',
  'oauth',
  'password-reset',
  'verify-email',
] as const);

/** The routed system surfaces by lower-case path, without a trailing slash. */
const SYSTEM_ROUTES: ReadonlyMap<string, SystemSurfaceName> = new Map<string, SystemSurfaceName>([
  ['/login', 'login'],
  ['/login/recovery', 'recovery-login'],
  ['/auth/callback', 'oauth-callback'],
  ['/oauth/consent', 'oauth-consent'],
  ['/logout', 'logout'],
  ['/verify-email', 'verify-email'],
  ['/password-reset', 'password-reset-request'],
  ['/password-reset/confirm', 'password-reset-confirm'],
  ['/invitations/accept', 'accept-invitation'],
  ['/change-password', 'change-password'],
]);

/**
 * Which system surface `pathname` is, the loader's first question once setup status reads
 * `ready`.
 *
 * The comparison ignores case and one trailing slash, as React Router does. The routes:
 * `/login`, `/login/recovery`, `/auth/callback`, `/oauth/consent`, `/logout`, `/verify-email`,
 * `/password-reset`, `/password-reset/confirm`, `/invitations/accept`, `/change-password`. A path
 * whose first segment is a {@link SYSTEM_ROUTE_SEGMENTS} entry and that matches no route answers
 * `not-found`. Any other path, `/`, `''` and `/system/x` included, answers `null` and goes to
 * root resolution. Pure; never throws.
 */
export function matchSystemRoute(pathname: string): SystemSurfaceName | null {
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const lower = trimmed.toLowerCase();
  const routed = SYSTEM_ROUTES.get(lower);
  if (routed !== undefined) return routed;
  const firstSegment = lower.split('/')[1] ?? '';
  return (SYSTEM_ROUTE_SEGMENTS as readonly string[]).includes(firstSegment) ? 'not-found' : null;
}

// ── Input ───────────────────────────────────────────────────────────────────

/** Whether the loader holds a session: still reading it, none, or one. */
export type SessionPresence = 'pending' | 'absent' | 'present';

/** The parts of the document location a system surface reads. */
export interface SystemLocation {
  readonly pathname: string;
  readonly search: string;
  readonly hostname: string;
}

/**
 * The brand row every system page heads with. `logoUrl` is resolved by the loader for the live
 * polarity, or null for the wordmark; `resolved` is false until the loader has read the org's
 * branding. Logos and `productName` stay branding (R-35).
 */
export interface SystemBrand {
  readonly resolved: boolean;
  readonly logoUrl: string | null;
  readonly productName: string;
}

/** The maintenance state the loader shows in `#zion-maintenance`: the seconds until its next try, and whether it is trying now. */
export interface MaintenanceNotice {
  readonly retryAfterSeconds: number;
  readonly retrying: boolean;
}

/** The input every system surface receives. `surface` names the page the one `mount` renders. */
export interface SystemSurfaceInput {
  readonly surface: SystemSurfaceName;
  readonly location: SystemLocation;
  readonly brand: SystemBrand;
  readonly session: SessionPresence;
  readonly maintenance: MaintenanceNotice | null;
}

/** The default each bad input field takes. */
const DEFAULT_LOCATION: SystemLocation = Object.freeze({ pathname: '/', search: '', hostname: '' });
const DEFAULT_BRAND: SystemBrand = Object.freeze({ resolved: false, logoUrl: null, productName: 'Console' });
const DEFAULT_SESSION: SessionPresence = 'pending';
const SESSION_PRESENCES: readonly string[] = Object.freeze(['pending', 'absent', 'present']);

/** True for a non-null object that is not an array. */
function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readLocation(value: unknown): SystemLocation {
  if (
    isRecord(value) &&
    typeof value.pathname === 'string' &&
    typeof value.search === 'string' &&
    typeof value.hostname === 'string'
  ) {
    return { pathname: value.pathname, search: value.search, hostname: value.hostname };
  }
  return DEFAULT_LOCATION;
}

function readBrand(value: unknown): SystemBrand {
  if (
    isRecord(value) &&
    typeof value.resolved === 'boolean' &&
    (value.logoUrl === null || typeof value.logoUrl === 'string') &&
    typeof value.productName === 'string'
  ) {
    return { resolved: value.resolved, logoUrl: value.logoUrl, productName: value.productName };
  }
  return DEFAULT_BRAND;
}

function isSessionPresence(value: unknown): value is SessionPresence {
  return typeof value === 'string' && SESSION_PRESENCES.includes(value);
}

function readSession(value: unknown): SessionPresence {
  return isSessionPresence(value) ? value : DEFAULT_SESSION;
}

function isSystemSurfaceName(value: unknown): value is SystemSurfaceName {
  return typeof value === 'string' && (SYSTEM_SURFACE_NAMES as readonly string[]).includes(value);
}

function readMaintenance(value: unknown): MaintenanceNotice | null {
  if (!isRecord(value) || typeof value.retrying !== 'boolean') return null;
  const seconds = value.retryAfterSeconds;
  const retryAfterSeconds = typeof seconds === 'number' && Number.isInteger(seconds) && seconds >= 0 ? seconds : 0;
  return { retryAfterSeconds, retrying: value.retrying };
}

/** The result of {@link systemInputParser}: the parsed input, or a refusal. */
export type SystemInputParseResult =
  | { readonly success: true; readonly data: SystemSurfaceInput }
  | { readonly success: false };

/**
 * The tolerant parser of a {@link SystemSurfaceInput}, the shape WS-3's `InputParser<T>` takes
 * (D8).
 *
 * It refuses only a value that is not an object, or whose `surface` is absent or not a
 * {@link SYSTEM_SURFACE_NAMES} entry. Every other field that is missing or malformed takes its
 * default: `location` `{pathname: '/', search: '', hostname: ''}` unless all three are strings;
 * `brand` `{resolved: false, logoUrl: null, productName: 'Console'}` unless all three fields have
 * their types; `session` `'pending'` unless it is one of the three presences; `maintenance`
 * `null` unless it is an object whose `retrying` is a boolean. Inside a kept `maintenance`, a
 * `retryAfterSeconds` that is not a non-negative integer (negative, fractional, infinite, NaN or
 * not a number) becomes `0`. The answer is a fresh object that carries no key beyond these five.
 * Pure; never throws.
 */
export const systemInputParser: { safeParse(value: unknown): SystemInputParseResult } = Object.freeze({
  safeParse(value: unknown): SystemInputParseResult {
    if (!isRecord(value)) return { success: false };
    const surface = value.surface;
    if (!isSystemSurfaceName(surface)) return { success: false };
    return {
      success: true,
      data: {
        surface,
        location: readLocation(value.location),
        brand: readBrand(value.brand),
        session: readSession(value.session),
        maintenance: readMaintenance(value.maintenance),
      },
    };
  },
});

// ── Outputs ─────────────────────────────────────────────────────────────────

/**
 * The four outputs a system surface emits, by name, with their payloads.
 *
 * - `session-established`: a sign-in finished. `session` is the minting route's body, which the
 *   loader parses with `sessionResponseSchema` before it stores anything; `landing` is where to go.
 * - `setup-completed`: the setup wizard finished. `session` is the owner's session, or null when
 *   the wizard minted none.
 * - `sign-out-requested`: the user asked to sign out. The loader ends the session through
 *   `POST /v1/auth/logout` and goes to `landing`.
 * - `second-factor-required`: a first factor passed and core-api answered a second-factor wait in
 *   place of a session (WS-25, R-012, WS-15.md R-6). `wait` is the `SecondFactorWait` of
 *   `@zion/sdk-contracts` that the minting route answered; `landing` is where to go after the
 *   second factor. The six pages that emit it: login, recovery-login, oauth-callback,
 *   accept-invitation, password-reset-confirm and change-password. The loader keeps the wait in
 *   memory only, never in a URL, a storage area or a log, and shows its second-factor page (the
 *   kernel shell, WS-15). The output carries no session, so a loader adopts nothing for it. Only
 *   a loader that subscribes to {@link KERNEL_SYSTEM_OUTPUT_NAMES} hears it; the shell loader
 *   subscribes to {@link SYSTEM_OUTPUT_NAMES} and never does. The runtime gives a page no answer
 *   to an emit, so after the emit each of the six pages shows one fixed sentence: the
 *   organization requires a second factor, and the page cannot ask for it. Under the shell loader
 *   that sentence is what the user sees. A loader that hears the output replaces the page. The
 *   accept-invitation, password-reset-confirm and change-password doors commit their write before
 *   the wait, so those three pages also replace their form with a status line and one action
 *   that emits `sign-out-requested` with the landing `/login` (review findings WS-25-R1, WS-25-R3).
 *
 * No output navigates on its own: a page calls `services.location.navigate`.
 */
export interface SystemOutputs {
  readonly 'session-established': { readonly session: unknown; readonly landing: string };
  readonly 'setup-completed': { readonly session: unknown; readonly landing: string };
  readonly 'sign-out-requested': { readonly landing: string };
  readonly 'second-factor-required': { readonly wait: SecondFactorWait; readonly landing: string };
}

/** One system output name. */
export type SystemOutputName = keyof SystemOutputs;

/**
 * The outputs every loader subscribes to and declares on the system mount: `session-established`,
 * `setup-completed` and `sign-out-requested`. Each one carries a `session` key, or is the sign-out
 * output, so a loader adopts a session for the first two and signs out for the third.
 *
 * The list leaves out `second-factor-required` (WS-25, R-012). The shell loader subscribes to
 * this list and has no second-factor page. So the runtime refuses that emit there as
 * `undeclared`, and the shell loader does not sign out for it. The page that emitted it then
 * shows its fixed second-factor sentence, so the user sees why the sign-in stopped (review
 * finding WS-25-R3). A loader that draws the second-factor page subscribes to
 * {@link KERNEL_SYSTEM_OUTPUT_NAMES}.
 */
export const SYSTEM_OUTPUT_NAMES: readonly SystemOutputName[] = Object.freeze([
  'session-established',
  'setup-completed',
  'sign-out-requested',
]);

/**
 * Every system output name: {@link SYSTEM_OUTPUT_NAMES} and then `second-factor-required`. A
 * loader that draws the second-factor page subscribes to this list and declares it on the system
 * mount: the kernel shell (WS-15.md section 3.5 step 6). Such a loader keeps the wait of a
 * `second-factor-required` output in memory and shows its second-factor page. A loader that
 * subscribes to this list must give each of the four names its own arm. A sign-out for the wait
 * output drops the wait and loops the user back to the login page.
 */
export const KERNEL_SYSTEM_OUTPUT_NAMES: readonly SystemOutputName[] = Object.freeze([
  ...SYSTEM_OUTPUT_NAMES,
  'second-factor-required',
]);

/**
 * True when `landing` is a safe same-origin path that is neither the logout route nor the tenant
 * hand-off callback page (customer onboarding S5, WS-15). `resolvePostLoginPath` answers its
 * argument exactly when the three rules hold, and otherwise `/home`, which passes all three.
 */
function isSafeLanding(landing: unknown): landing is string {
  return typeof landing === 'string' && resolvePostLoginPath(landing) === landing;
}

type OutputReaders = { readonly [K in SystemOutputName]: (payload: Readonly<Record<string, unknown>>) => SystemOutputs[K] | null };

const OUTPUT_READERS: OutputReaders = Object.freeze({
  'session-established': (payload) =>
    isSafeLanding(payload.landing) && isRecord(payload.session)
      ? { session: payload.session, landing: payload.landing }
      : null,
  'setup-completed': (payload) =>
    isSafeLanding(payload.landing) && (payload.session === null || isRecord(payload.session))
      ? { session: payload.session, landing: payload.landing }
      : null,
  'sign-out-requested': (payload) => (isSafeLanding(payload.landing) ? { landing: payload.landing } : null),
  'second-factor-required': (payload) => {
    if (!isSafeLanding(payload.landing)) return null;
    const wait = secondFactorWaitSchema.safeParse(payload.wait);
    return wait.success ? { wait: wait.data, landing: payload.landing } : null;
  },
});

/**
 * True when `value` is one of {@link KERNEL_SYSTEM_OUTPUT_NAMES}, the four names of
 * {@link SystemOutputs}; an inherited name such as `constructor` answers false.
 */
export function isSystemOutputName(value: unknown): value is SystemOutputName {
  return typeof value === 'string' && Object.hasOwn(OUTPUT_READERS, value);
}

/**
 * The first step of session adoption: the typed payload of a system output, or `null` to drop
 * the event.
 *
 * The loader runs it on every emit from the system mount, and only that mount's outputs are ever
 * read. It answers `null` when `name` is not a {@link KERNEL_SYSTEM_OUTPUT_NAMES} entry, when `payload`
 * is not an object, when `landing` is not a safe same-origin path (`isSafeReturnTo`) or is the
 * logout route, and when `session` is not a non-null object; `setup-completed` also takes
 * `session: null`. For `second-factor-required` it also answers `null` when `wait` fails the
 * strict `secondFactorWaitSchema` of `@zion/sdk-contracts`: a status other than
 * `second_factor_required`, a mode outside the four modes, a token that is empty, longer than
 * 4,096 characters or holds a character outside `[A-Za-z0-9._-]`, an `expiresIn` that is not an
 * integer from 1 to 300, or an extra key. A passing answer is a fresh object with only the
 * documented keys; `session` is passed through untouched for the loader's `sessionResponseSchema`
 * parse, and `wait` is the schema's fresh copy. It never throws.
 */
export function readSystemOutput<K extends SystemOutputName>(name: K, payload: unknown): SystemOutputs[K] | null;
export function readSystemOutput(name: string, payload: unknown): SystemOutputs[SystemOutputName] | null;
export function readSystemOutput(name: string, payload: unknown): SystemOutputs[SystemOutputName] | null {
  if (!isSystemOutputName(name) || !isRecord(payload)) return null;
  return OUTPUT_READERS[name](payload);
}
